import { useContext, useEffect, useState } from 'react'
import { FormikError, Loader } from '@nv/basic-components'
import { ModalContext } from '@nv/contexts'
import WarningIcon from '@nv/images/design/warning-icon'
import { useFormik } from 'formik'
import { t } from 'i18next'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import DocumentInputUpload from './document_input_upload/DocumentInputUpload'

const initialValues = {
  englishTitle: '',
  frenchTitle: '',
  englishDocument: null,
  frenchDocument: null,
}

const AddDocument = ({ formik, setUploadErr, setCurrentView }) => (
  <form>
    <DocumentInputUpload
      formik={formik}
      textFieldName='englishTitle'
      documentFieldName='englishDocument'
      label={t('uploads.document.labels.en')}
      setUploadErr={(error) => {
        setUploadErr(error)
        setCurrentView('uploadError')
      }}
    />
    {formik.values['englishTitle'] && <FormikError formik={formik} fieldName='englishDocument' />}

    <DocumentInputUpload
      formik={formik}
      textFieldName='frenchTitle'
      documentFieldName='frenchDocument'
      label={t('uploads.document.labels.fr')}
      style={{ marginTop: 40 }}
      setUploadErr={(error) => {
        setUploadErr(error)
        setCurrentView('uploadError')
      }}
    />
    {formik.values['frenchTitle'] && <FormikError formik={formik} fieldName='frenchDocument' />}
  </form>
)

// technically this is an AddDocModal not specific to events but it was named this in the previous version of code
export const EventAttach = ({ modalOpen, eventId, getAttachments, onSubmitUpload }: any) => {
  const { t } = useTranslation()
  const [currentView, setCurrentView] = useState('Add Document')
  const [uploadErr, setUploadErr] = useState('')
  const { setHeaderLabel, setBtns, onClose } = useContext(ModalContext)

  const onClosing = () => {
    setCurrentView('Add Document')
    onClose()
  }

  const validationSchema = Yup.object().shape({
    frenchTitle: Yup.string(),
    englishTitle: Yup.string().when('frenchTitle', {
      is: (arg) => arg == null,
      then: Yup.string().required(t('uploads.document.validations.title')),
      otherwise: Yup.string(),
    }),
    englishDocument: Yup.mixed().when('englishTitle', {
      is: (arg) => arg != null,
      then: Yup.mixed().required(t('uploads.document.validations.englishRequired')),
      otherwise: Yup.mixed(),
    }),
    frenchDocument: Yup.mixed().when('frenchTitle', {
      is: (arg) => arg != null,
      then: Yup.mixed().required(t('uploads.document.validations.frenchRequired')),
      otherwise: Yup.mixed(),
    }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setCurrentView('loadingScreen')
        const formData = new FormData()
        formData.append('en-helpfile', values.englishDocument?.file)
        formData.set('en-title', values.englishTitle)
        formData.append('fr-helpfile', values.frenchDocument?.file)
        formData.set('fr-title', values.frenchTitle)

        await onSubmitUpload(formData, eventId)

        onClose()
        getAttachments()
      } catch (error) {
        setUploadErr(t('errors.contactSupport'))
        setCurrentView('uploadError')
      }
    },
  })

  useEffect(() => {
    formik.resetForm()
  }, [modalOpen])

  useEffect(() => {
    if (
      (formik.values['englishTitle'] && formik.values['englishDocument']) ||
      (formik.values['frenchTitle'] && formik.values['frenchDocument'])
    ) {
      // Update validation schema if necessary
    }
  }, [formik.errors])

  const renderView = () => {
    if (currentView === 'Add Document') return <AddDocument formik={formik} setUploadErr={setUploadErr} setCurrentView={setCurrentView} />
    if (currentView === 'uploadError')
      return (
        <>
          <WarningIcon color={'var(--error-color)'} />
          <div>{uploadErr}</div>
        </>
      )
    if (currentView === 'loadingScreen')
      return (
        <div style={{ padding: '2rem' }}>
          <Loader />
        </div>
      )
  }

  useEffect(() => {
    setHeaderLabel(t('uploads.document.add'))
    setBtns(
      currentView === 'loadingScreen'
        ? [
            {
              text: t('buttons.close'),
              onClick: onClosing,
            },
          ]
        : [
            {
              text: t('buttons.cancel'),
              onClick: onClosing,
              variant: 'secondary',
            },
            {
              text: t('buttons.add'),
              onClick: formik.handleSubmit,
            },
          ],
    )
  }, [currentView])

  return renderView()
}

export default EventAttach
